import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CustomReportsAPI from '../../api/customReports';
import { throwErrorMessage } from '../utils/api';

// Cache local de previews: Map<JSON_string, result>
const previewCache = new Map();
const PREVIEW_CACHE_MAX = 50;

function hashQuery(queryDefinition) {
  return JSON.stringify(queryDefinition || {});
}

function getCachedPreview(queryDefinition) {
  return previewCache.get(hashQuery(queryDefinition));
}

function setCachedPreview(queryDefinition, data) {
  if (previewCache.size >= PREVIEW_CACHE_MAX) {
    const firstKey = previewCache.keys().next().value;
    previewCache.delete(firstKey);
  }
  previewCache.set(hashQuery(queryDefinition), data);
}

function clearPreviewCache() {
  previewCache.clear();
}

// A API responde 422 quando RECUSA a definição do painel (fora do catálogo,
// forma errada), com o mesmo corpo `{ valid: false, errors }` que a tela exibe.
// O axios trata 4xx como exceção; aqui a recusa volta a ser o resultado que era.
function refusedPreview(error) {
  const data = error?.response?.data;
  if (error?.response?.status !== 422 || !data || data.valid !== false) {
    return null;
  }
  return data;
}

export const state = {
  records: [],
  currentReport: null,
  catalog: null,
  widgets: [],
  runs: [],
  currentRun: null,
  exports: [],
  currentExport: null,
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isDuplicating: false,
    isFetchingCatalog: false,
    isFetchingWidgets: false,
    isPreviewing: false,
    isBatchPreviewing: false,
    isRunning: false,
    isFetchingRuns: false,
    isExporting: false,
    isFetchingExports: false,
    // A listagem voltou 403: o portão do módulo (#1398) nega a conta sem a
    // feature flag. A tela mostra "módulo indisponível" em vez de lista vazia.
    isModuleUnavailable: false,
    // A listagem falhou por outro motivo (500, rede). Sem esta marca a tela
    // caía no "nenhum relatório" — erro vestido de lista vazia (#1396).
    hasFetchError: false,
    // O catálogo falhou. Ele alimenta o editor, o formulário de metas e os
    // filtros globais; vazio por erro parecia "conta sem fonte de dados".
    hasCatalogError: false,
  },
};

export const getters = {
  getCustomReports($state) {
    return $state.records;
  },
  getCustomReport: $state => id => {
    return (
      $state.records.find(record => record.id === Number(id)) ||
      $state.currentReport
    );
  },
  getCurrentReport($state) {
    return $state.currentReport;
  },
  getCatalog($state) {
    return $state.catalog;
  },
  getWidgets($state) {
    return $state.widgets;
  },
  getRuns($state) {
    return $state.runs;
  },
  getCurrentRun($state) {
    return $state.currentRun;
  },
  getExports($state) {
    return $state.exports;
  },
  getCurrentExport($state) {
    return $state.currentExport;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  fetch: async function fetchCustomReports({ commit }, params = {}) {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetching: true });
    try {
      const response = await CustomReportsAPI.get(params);
      commit(types.SET_CUSTOM_REPORTS, response.data);
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isModuleUnavailable: false });
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { hasFetchError: false });
    } catch (error) {
      if (error && error.response && error.response.status === 403) {
        commit(types.SET_CUSTOM_REPORTS, []);
        commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isModuleUnavailable: true });
        commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { hasFetchError: false });
        return;
      }
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { hasFetchError: true });
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetching: false });
    }
  },
  show: async function showCustomReport({ commit }, id) {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await CustomReportsAPI.getReport(id);
      commit(types.SET_CURRENT_CUSTOM_REPORT, response.data);
      commit(types.ADD_CUSTOM_REPORT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingItem: false });
    }
    return null;
  },
  create: async function createCustomReport({ commit }, reportObj) {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isCreating: true });
    try {
      const response = await CustomReportsAPI.create(reportObj);
      commit(types.ADD_CUSTOM_REPORT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isCreating: false });
    }
    return null;
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await CustomReportsAPI.update(id, updateObj);
      commit(types.EDIT_CUSTOM_REPORT, response.data);
      commit(types.SET_CURRENT_CUSTOM_REPORT, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isDeleting: true });
    try {
      await CustomReportsAPI.delete(id);
      commit(types.DELETE_CUSTOM_REPORT, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isDeleting: false });
    }
  },
  duplicate: async ({ commit }, id) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isDuplicating: true });
    try {
      const response = await CustomReportsAPI.duplicate(id);
      commit(types.ADD_CUSTOM_REPORT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isDuplicating: false });
    }
    return null;
  },
  archive: async ({ commit }, id) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await CustomReportsAPI.archive(id);
      commit(types.EDIT_CUSTOM_REPORT, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isUpdating: false });
    }
  },
  restore: async ({ commit }, id) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await CustomReportsAPI.restore(id);
      commit(types.EDIT_CUSTOM_REPORT, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isUpdating: false });
    }
  },
  favorite: async ({ commit }, id) => {
    try {
      await CustomReportsAPI.favorite(id);
      commit(types.EDIT_CUSTOM_REPORT, { id, favorited: true });
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  unfavorite: async ({ commit }, id) => {
    try {
      await CustomReportsAPI.unfavorite(id);
      commit(types.EDIT_CUSTOM_REPORT, { id, favorited: false });
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  fetchCatalog: async ({ commit }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingCatalog: true });
    try {
      const response = await CustomReportsAPI.catalog();
      commit(types.SET_CUSTOM_REPORTS_CATALOG, response.data);
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { hasCatalogError: false });
    } catch (error) {
      // Não rejeita (três telas chamam sem esperar), mas também não finge que
      // o catálogo veio vazio: quem o usa olha `hasCatalogError`.
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { hasCatalogError: true });
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingCatalog: false });
    }
  },
  validateQuery: async (_ctx, queryDefinition) => {
    return CustomReportsAPI.validateQuery(queryDefinition);
  },
  fetchWidgets: async ({ commit }, reportId) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingWidgets: true });
    try {
      const response = await CustomReportsAPI.getWidgets(reportId);
      commit(types.SET_CUSTOM_REPORTS_WIDGETS, response.data);
    } catch (error) {
      // Engolir aqui fazia a tela dizer "este relatório não tem painéis".
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingWidgets: false });
    }
  },
  showWidget: async ({ commit }, { reportId, widgetId }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await CustomReportsAPI.getWidget(reportId, widgetId);
      commit(types.EDIT_CUSTOM_REPORT_WIDGET, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingItem: false });
    }
  },
  createWidget: async ({ commit }, { reportId, ...widget }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isCreating: true });
    try {
      const response = await CustomReportsAPI.createWidget(reportId, widget);
      commit(types.ADD_CUSTOM_REPORT_WIDGET, response.data);
      clearPreviewCache();
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isCreating: false });
    }
    return null;
  },
  updateWidget: async ({ commit }, { reportId, id, ...widget }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isUpdating: true });
    try {
      const response = await CustomReportsAPI.updateWidget(
        reportId,
        id,
        widget
      );
      commit(types.EDIT_CUSTOM_REPORT_WIDGET, response.data);
      clearPreviewCache();
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isUpdating: false });
    }
  },
  deleteWidget: async ({ commit }, { reportId, id }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isDeleting: true });
    try {
      await CustomReportsAPI.deleteWidget(reportId, id);
      commit(types.DELETE_CUSTOM_REPORT_WIDGET, id);
      clearPreviewCache();
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isDeleting: false });
    }
  },
  previewWidget: async ({ commit }, { reportId, id }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isPreviewing: true });
    try {
      const response = await CustomReportsAPI.previewWidget(reportId, id);
      return response.data;
    } catch (error) {
      const refused = refusedPreview(error);
      if (refused) return refused;
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isPreviewing: false });
    }
    return null;
  },
  previewQuery: async (_ctx, { reportId, queryDefinition }) => {
    const cached = getCachedPreview(queryDefinition);
    if (cached) return cached;

    let response;
    try {
      response = await CustomReportsAPI.previewQuery(reportId, queryDefinition);
    } catch (error) {
      const refused = refusedPreview(error);
      if (refused) return refused;
      throw error;
    }
    if (response.data && response.data.valid !== false) {
      setCachedPreview(queryDefinition, response.data);
    }
    return response.data;
  },
  batchPreview: async ({ commit, state }, { reportId, crossFilters } = {}) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isBatchPreviewing: true });
    try {
      const response = await CustomReportsAPI.batchPreview(
        reportId,
        crossFilters
      );
      const results = response.data.results || {};

      // Cache individual widget results
      const widgets = state.widgets || [];
      widgets.forEach(w => {
        if (results[w.id] && results[w.id].valid !== false) {
          setCachedPreview(w.query_definition, results[w.id]);
        }
      });

      return results;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isBatchPreviewing: false });
    }
    return null;
  },
  clearPreviewCache: () => {
    clearPreviewCache();
  },
  reorderWidgets: async (
    { commit, state },
    { reportId, items, optimisticOrder }
  ) => {
    const previousOrder = state.widgets.slice();
    if (Array.isArray(optimisticOrder)) {
      commit(types.SET_CUSTOM_REPORTS_WIDGETS_RAW, optimisticOrder);
    }
    try {
      const response = await CustomReportsAPI.reorderWidgets(reportId, items);
      commit(types.SET_CUSTOM_REPORTS_WIDGETS, response.data);
      return response.data;
    } catch (error) {
      commit(types.SET_CUSTOM_REPORTS_WIDGETS_RAW, previousOrder);
      throwErrorMessage(error);
      throw error;
    }
  },
  // ── Runs ──────────────────────────────────────────
  runReport: async ({ commit }, reportId) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isRunning: true });
    try {
      const response = await CustomReportsAPI.runReport(reportId);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isRunning: false });
    }
    return null;
  },
  fetchRuns: async ({ commit }, reportId) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingRuns: true });
    try {
      const response = await CustomReportsAPI.getRuns(reportId);
      commit(types.SET_CUSTOM_REPORT_RUNS, response.data);
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingRuns: false });
    }
  },
  fetchRun: async ({ commit }, { reportId, runId }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await CustomReportsAPI.getRun(reportId, runId);
      commit(types.SET_CURRENT_CUSTOM_REPORT_RUN, response.data);
      return response.data;
    } catch (error) {
      // Devolver `null` fazia o polling da execução rodar para sempre.
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingItem: false });
    }
  },
  cancelRun: async ({ commit }, { reportId, runId }) => {
    try {
      const response = await CustomReportsAPI.cancelRun(reportId, runId);
      commit(types.SET_CURRENT_CUSTOM_REPORT_RUN, response.data);
    } catch (error) {
      throwErrorMessage(error);
    }
  },
  // ── Exports ───────────────────────────────────────
  createExport: async ({ commit }, { reportId, file_format, parameters }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isExporting: true });
    try {
      const response = await CustomReportsAPI.createExport(reportId, {
        file_format,
        parameters,
      });
      commit(types.ADD_CUSTOM_REPORT_EXPORT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isExporting: false });
    }
    return null;
  },
  fetchExports: async ({ commit }, reportId) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingExports: true });
    try {
      const response = await CustomReportsAPI.getExports(reportId);
      commit(types.SET_CUSTOM_REPORT_EXPORTS, response.data);
    } catch (error) {
      // Ignore
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingExports: false });
    }
  },
  fetchExport: async ({ commit }, { reportId, exportId }) => {
    commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await CustomReportsAPI.getExport(reportId, exportId);
      commit(types.SET_CURRENT_CUSTOM_REPORT_EXPORT, response.data);
      return response.data;
    } catch (error) {
      // Devolver `null` fazia o polling do export rodar para sempre.
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_CUSTOM_REPORTS_UI_FLAG, { isFetchingItem: false });
    }
  },
  downloadExport: async (_ctx, { reportId, exportId }) => {
    try {
      const response = await CustomReportsAPI.downloadExport(
        reportId,
        exportId
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?(.+?)"?$/);
      link.download = match ? match[1] : 'export.csv';
      link.click();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      throwErrorMessage(error);
      return false;
    }
  },
};

export const mutations = {
  [types.SET_CUSTOM_REPORTS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.SET_CUSTOM_REPORTS]: ($state, data) => {
    $state.records = data.payload || data;
  },
  [types.ADD_CUSTOM_REPORT]: MutationHelpers.setSingleRecord,
  [types.EDIT_CUSTOM_REPORT]: MutationHelpers.updateAttributes,
  [types.DELETE_CUSTOM_REPORT]: MutationHelpers.destroy,
  [types.SET_CURRENT_CUSTOM_REPORT]: ($state, data) => {
    $state.currentReport = data;
  },
  [types.SET_CUSTOM_REPORTS_CATALOG]: ($state, data) => {
    $state.catalog = data.datasets || [];
  },
  [types.SET_CUSTOM_REPORTS_WIDGETS]: ($state, data) => {
    $state.widgets = data.payload || [];
  },
  [types.SET_CUSTOM_REPORTS_WIDGETS_RAW]: ($state, data) => {
    $state.widgets = Array.isArray(data) ? data : [];
  },
  [types.ADD_CUSTOM_REPORT_WIDGET]: ($state, data) => {
    if (!$state.widgets.find(w => w.id === data.id)) {
      $state.widgets.push(data);
    }
  },
  [types.EDIT_CUSTOM_REPORT_WIDGET]: ($state, data) => {
    const index = $state.widgets.findIndex(w => w.id === data.id);
    if (index > -1) {
      $state.widgets.splice(index, 1, {
        ...$state.widgets[index],
        ...data,
      });
    }
  },
  [types.DELETE_CUSTOM_REPORT_WIDGET]: ($state, id) => {
    $state.widgets = $state.widgets.filter(w => w.id !== id);
  },
  // ── Runs ──
  [types.SET_CUSTOM_REPORT_RUNS]: ($state, data) => {
    $state.runs = data.payload || [];
  },
  [types.SET_CURRENT_CUSTOM_REPORT_RUN]: ($state, data) => {
    $state.currentRun = data;
  },
  // ── Exports ──
  [types.SET_CUSTOM_REPORT_EXPORTS]: ($state, data) => {
    $state.exports = data.payload || [];
  },
  [types.SET_CURRENT_CUSTOM_REPORT_EXPORT]: ($state, data) => {
    $state.currentExport = data;
  },
  [types.ADD_CUSTOM_REPORT_EXPORT]: ($state, data) => {
    if (!$state.exports.find(e => e.id === data.id)) {
      $state.exports.push(data);
    }
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
